import { useMemo } from "react"
import { add, allocate, Dinero } from "dinero.js"
import { DateTimeFactory, useDateTime } from "../../localization/useDateTime"
import { BuyerParty, IMyOrderDetails, IOrderLine, OrderType } from "../../payments/orderTypes"
import { COMPANY_PAYMENT_METHOD, PaymentMethodType } from "../../payments/paymentTypes"
import { Comment, HeaderSection, ReceiptContainer } from "../components/ReceiptBlocks"
import { getPaymentMethodType, PurchaseDetailsSection } from "../components/PurchaseDetailsSection"
import { DeliveryDetailsSection } from "../components/DeliveryDetailsSection"
import { DineroOrderLine, FlatOrderLineGroup, OrderDetailsSection } from "../components/OrderDetailsSection"

import { toCurrency, toDineroOrUndefined, zeroDinero } from "../../currency/currencyLib"
import { SubscriptionDetailsSection } from "../components/SubscriptionDetailsSection"
import { ErrorBoundary } from "react-error-boundary"
import { ErrorFallback } from "./ErrorFallback"

type GroupedOrderLines = Record<BuyerParty, Record<PaymentMethodType, IOrderLine[]>>

function getOrderDisplayName(orderId: number, order: IMyOrderDetails) {
    return order?.displayName ?? `Receipt #${orderId.toString()}`
}

function getPaymentDetails(order: IMyOrderDetails) {
    return order?.paymentDetails
}

function getKitchen(order: IMyOrderDetails) {
    return order?.kitchen
}

function getCustomer(order: IMyOrderDetails) {
    return order?.organizers?.[0]
}

function getTimeOfPurchase(order: IMyOrderDetails, dateTimeFactory: DateTimeFactory) {
    const created = order?.created
    return created ? dateTimeFactory.fromApi(created) : undefined
}

function getDelivery(order: IMyOrderDetails) {
    return order?.deliveries?.[0]
}

function getDeliveryComment(order: IMyOrderDetails): Comment | undefined {
    if (!order) return undefined

    const loadedComment = order.deliveries?.[0].orderNote
    if (!loadedComment || loadedComment.trim() === "") return { present: false }

    return { present: true, text: loadedComment }
}

function getOrderLinePaymentMethod(line: IOrderLine, orderPaymentMethod: PaymentMethodType) {
    if (line.paymentDetails?.method) return line.paymentDetails.method
    if (line.buyerParty === BuyerParty.COMPANY) return COMPANY_PAYMENT_METHOD
    return getPaymentMethodType(orderPaymentMethod)
}

function getIncludedVAT(totalPrice: Dinero<number>) {
    const dkVatRate = 25 // TODO: Need to take from response when available
    const [totalBeforeVat, vat] = allocate(totalPrice, [100 - dkVatRate, dkVatRate])
    return vat
}

function getGroupedOrderLines(order: IMyOrderDetails) {
    // Todays orders does not contain payment methods for order
    if (!order) return undefined

    const orderLines = order.deliveries?.[0].orderLines
    if (!orderLines) return undefined

    if (!order.paymentDetails?.method) return undefined
    const orderPaymentMethod = order.paymentDetails.method

    return orderLines.reduce((acc, line) => {
        if (line.buyerParty) {
            const paymentMethod = getOrderLinePaymentMethod(line, orderPaymentMethod)

            let buyerPartyGroups = acc[line.buyerParty]
            if (!buyerPartyGroups) {
                buyerPartyGroups = {} as Record<PaymentMethodType, IOrderLine[]>
                acc[line.buyerParty] = buyerPartyGroups
            }

            let paymentMethodLines = buyerPartyGroups[paymentMethod]
            if (!paymentMethodLines) {
                paymentMethodLines = []
                buyerPartyGroups[paymentMethod] = paymentMethodLines
            }

            paymentMethodLines.push(line)
        }
        return acc
    }, {} as GroupedOrderLines)
}

function toDineroOrderLine(line: IOrderLine) {
    const price = toDineroOrUndefined(line.price)

    const result: DineroOrderLine = {
        ...line,
        price,
    }

    return result
}

function flattenGroupedOrderLines(orderLines: GroupedOrderLines | undefined) {
    if (!orderLines) return undefined

    // TODO: Take currency from response
    const zero = zeroDinero(toCurrency("DKK"))

    return Object.entries(orderLines).reduce((allGroups, [buyerParty, group]) => {
        return Object.entries(group).reduce((bpGroups, [paymentMethod, ol]) => {
            const totalPrice = ol.reduce((sum, line) => add(sum, toDineroOrUndefined(line.price) ?? zero), zero)
            const includedVAT = getIncludedVAT(totalPrice)

            const flatGroup: FlatOrderLineGroup = {
                buyerParty: buyerParty as BuyerParty,
                paymentMethod: paymentMethod as PaymentMethodType,
                orderLines: ol.map(toDineroOrderLine),
                totalPrice,
                includedVAT,
            }
            bpGroups.push(flatGroup)
            return bpGroups
        }, allGroups)
    }, [] as FlatOrderLineGroup[])
}

function getOrderLines(order: IMyOrderDetails) {
    return order?.orderLines
}

function getSubscriptionDetails(order: IMyOrderDetails) {
    return order?.subscriptionDetails
}

type ReceiptDetailsProps = {
    orderId: number
    order: IMyOrderDetails
}

export const ReceiptDetails = ({ orderId, order }: ReceiptDetailsProps) => {
    const dateTimeFactory = useDateTime()

    const groupedOrderLines = useMemo(() => flattenGroupedOrderLines(getGroupedOrderLines(order)), [order])

    return (
        <ErrorBoundary
            FallbackComponent={() => (
                <ReceiptContainer>
                    <ErrorFallback orderId={order?.id} />
                </ReceiptContainer>
            )}
        >
            <ReceiptContainer>
                <HeaderSection>{getOrderDisplayName(orderId, order)}</HeaderSection>

                <PurchaseDetailsSection
                    paymentDetails={getPaymentDetails(order)}
                    kitchen={getKitchen(order)}
                    customer={getCustomer(order)}
                    timeOfPurchase={getTimeOfPurchase(order, dateTimeFactory)}
                    orderId={`${orderId}`}
                    shopChannel={order?.shopChannel}
                />

                {/*
                <ActionsSection
                    orderId={orderId}
                    orderUid={order?.uid}
                    delivery={getDelivery(orderResponse)}
                />
            */}

                <DeliveryDetailsSection
                    orderType={order.orderType}
                    delivery={getDelivery(order)}
                    deliveryComment={getDeliveryComment(order)}
                />

                {groupedOrderLines ? (
                    <>
                        {groupedOrderLines.map((group, index, array) => (
                            <OrderDetailsSection key={index} orderDetails={group} final={index === array.length - 1} />
                        ))}
                    </>
                ) : (
                    <OrderDetailsSection final={true} />
                )}

                {order.orderType === OrderType.LUNCH_SUBSCRIPTION && (
                    <SubscriptionDetailsSection
                        subscription={getSubscriptionDetails(order)}
                        orderLines={getOrderLines(order)}
                    />
                )}
            </ReceiptContainer>
        </ErrorBoundary>
    )
}
